import React, { useEffect, useState } from 'react';

const DEFAULT_CONFETTI_COLORS = ['#a65241', '#1a6e7a', '#d4c4a8', '#f5f0e6'];

export default function TaskDoneAnimation({ taskTitle, onComplete, confettiColors = null }) {
  const [isVisible, setIsVisible] = useState(true);
  const [confetti, setConfetti] = useState([]);

  useEffect(() => {
    const particles = [];
    const colors = confettiColors || DEFAULT_CONFETTI_COLORS;
    for (let i = 0; i < 30; i++) {
      particles.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 8 + Math.random() * 8,
      });
    }
    setConfetti(particles);

    // Hide animation after 3.5 seconds
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) {
        setTimeout(onComplete, 500); // Wait for fade out
      }
    }, 3500);

    return () => clearTimeout(hideTimer);
  }, [onComplete, taskTitle, confettiColors]);

  if (!isVisible) return null;

  return (
    <div className="task-done-animation">
      {/* Confetti particles */}
      <div className="confetti-container">
        {confetti.map((particle) => (
          <div
            key={particle.id}
            className="confetti-particle"
            style={{
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              backgroundColor: particle.color,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
            }}
          />
        ))}
      </div>

      {/* Main checkmark and text */}
      <div className="celebration-content">
        <div className="checkmark-container">
          <svg className="checkmark" viewBox="0 0 100 100">
            <circle className="checkmark-circle" cx="50" cy="50" r="45" />
            <path
              className="checkmark-check"
              d="M 30 50 L 45 65 L 70 35"
              fill="none"
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="done-text">DONE!</div>
        {taskTitle && (
          <div className="task-title">{taskTitle}</div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');

        .task-done-animation {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 1000;
          pointer-events: none;
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        .confetti-container {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
        }

        .confetti-particle {
          position: absolute;
          top: -10px;
          width: 8px;
          height: 8px;
          background: #a65241;
          animation: confettiFall linear forwards;
          image-rendering: pixelated;
        }

        @keyframes confettiFall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        .celebration-content {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 20px;
          padding: 40px;
          background: rgba(13, 74, 84, 0.95);
          border: 4px solid #d4c4a8;
          box-shadow: 
            6px 6px 0 #0d4a54,
            0 0 30px rgba(166, 82, 65, 0.6),
            inset 0 0 0 2px rgba(212, 196, 168, 0.3);
          min-width: 300px;
        }

        .checkmark-container {
          width: 120px;
          height: 120px;
          animation: checkmarkPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        @keyframes checkmarkPop {
          0% {
            transform: scale(0) rotate(-180deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.1) rotate(10deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        .checkmark {
          width: 100%;
          height: 100%;
        }

        .checkmark-circle {
          fill: #1a6e7a;
          stroke: #d4c4a8;
          stroke-width: 4;
          animation: circlePulse 0.6s ease-out;
        }

        @keyframes circlePulse {
          0% {
            r: 0;
            opacity: 0;
          }
          100% {
            r: 45;
            opacity: 1;
          }
        }

        .checkmark-check {
          stroke: #d4c4a8;
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: checkmarkDraw 0.5s ease-out 0.3s forwards;
        }

        @keyframes checkmarkDraw {
          to {
            stroke-dashoffset: 0;
          }
        }

        .done-text {
          font-family: 'Press Start 2P', monospace;
          font-size: 2rem;
          color: #f5f0e6;
          text-shadow: 
            3px 3px 0 #a65241,
            0 0 10px rgba(245, 240, 230, 0.5);
          animation: textBounce 0.6s ease-out 0.8s both;
          letter-spacing: 0.1em;
        }

        @keyframes textBounce {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .task-title {
          font-family: 'Press Start 2P', monospace;
          font-size: 0.7rem;
          color: #d4c4a8;
          text-align: center;
          max-width: 250px;
          word-wrap: break-word;
          line-height: 1.5;
          animation: fadeInUp 0.5s ease-out 1.2s both;
          text-shadow: 1px 1px 0 #0d4a54;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

